import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import axiosInstance from './api/axiosInstance';
import './App.css';
import TaskComponent from './components/TaskComponent';
import Modal from './components/createTaskModal';
import MainPage from './components/MainPage';
import HomePage from './components/HomePage';
import NavigationBar from './components/NavigationBar';
import SupervisorPage from './components/SupervisorPage';
import TaskDetailModal from './components/TaskDetailModal';
import SchedulePage from './components/SchedulePage';
import ContractorManagementPage from './components/ContractorManagementPage';
import WorkOrderPage from './components/WorkOrderPage';
import LoginPage from './components/LoginPage';
import PrivateRoute from './components/PrivateRoute';
import UserManagementPage from './components/UserManagementPage'; // UserManagementPage 임포트
import AssociationReportManagementPage from './components/AssociationReportManagementPage'; // 협회신고관리 페이지 임포트
import StatisticsPage from './components/StatisticsPage'; // StatisticsPage 임포트
import CompanyInfoPage from './components/CompanyInfoPage'; // CompanyInfoPage 임포트
import DownloadsPage from './components/DownloadsPage'; // DownloadsPage 임포트
import BranchManagementPage from './components/BranchManagementPage'; // BranchManagementPage 임포트
import { useSetAtom, useAtomValue } from 'jotai';
import { tasksState, scheduleEntriesState, supervisorsListState, supervisorAssignmentDaysState, contractorsState, branchesState } from './jotai/atoms';
import Swal from 'sweetalert2';
import { useAuth } from './context/AuthContext';
import ReactModal from 'react-modal';
import branchService from './api/branchService';

ReactModal.setAppElement('#root');

function App() {
  const { user } = useAuth();
  const location = useLocation();
  const setTasks = useSetAtom(tasksState);
  const setSupervisorsList = useSetAtom(supervisorsListState);
  const setScheduleEntries = useSetAtom(scheduleEntriesState);
  const setSupervisorAssignmentDays = useSetAtom(supervisorAssignmentDaysState);
  const setContractors = useSetAtom(contractorsState);
  const setBranches = useSetAtom(branchesState);

  const tasks = useAtomValue(tasksState);
  const supervisorsList = useAtomValue(supervisorsListState);
  const contractors = useAtomValue(contractorsState);
  const branches = useAtomValue(branchesState);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isTaskDetailEditMode, setIsTaskDetailEditMode] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [newTask, setNewTask] = useState({
    displayNumber: '',
    name: '',
    number: '',
    headquarters: '',
    branch: '',
    managers: [], // manager -> managers (배열)
    assistants: [], // assistant -> assistants (배열)
    period: '',
    company: '',
    contractDate: '',
    constructionPeriod: '',
    remarks: '',
    contractAmount: '',
    completionAmount: '0',
    taskStatus: '공사중',
    completionReportStatus: '미완료',
  });

  const newTaskRef = useRef(newTask);
  useEffect(() => {
    newTaskRef.current = newTask;
  }, [newTask]);

  // All the fetch and handler functions remain the same
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    
    let formattedValue = value;

    if (name === 'headquarters') {
      setNewTask(prev => ({ ...prev, headquarters: value, branch: '' }));
      return;
    }

    if (name === 'number' || name === 'mainTaskNumber') {
      // 대문자 알파벳과 숫자만 남기고 모든 문자 제거
      formattedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      // 4자리-4자리-5자리 형식으로 하이픈 추가 (이 부분은 필요에 따라 조정할 수 있습니다)
      if (formattedValue.length > 4 && formattedValue.length <= 8) {
        formattedValue = formattedValue.replace(/([A-Z0-9]{4})([A-Z0-9]+)/, '$1-$2');
      } else if (formattedValue.length > 8) {
        formattedValue = formattedValue.replace(/([A-Z0-9]{4})([A-Z0-9]{4})([A-Z0-9]+)/, '$1-$2-$3');
      }
    } else if (name === 'constructionPeriod') {
      let rawValue = value.replace(/[^0-9]/g, ''); // 숫자만 남김
      let formatted = '';

      if (rawValue.length > 0) {
        formatted += rawValue.substring(0, 4); // YYYY
      }
      if (rawValue.length >= 5) {
        formatted += '-' + rawValue.substring(4, 6); // -MM
      }
      if (rawValue.length >= 7) {
        formatted += '-' + rawValue.substring(6, 8); // -DD
      }
      if (rawValue.length >= 9) {
        formatted += ' ~ ' + rawValue.substring(8, 12); // ~ YYYY
      }
      if (rawValue.length >= 13) {
        formatted += '-' + rawValue.substring(12, 14); // -MM
      }
      if (rawValue.length >= 15) {
        formatted += '-' + rawValue.substring(14, 16); // -DD
      }
      formattedValue = formatted;
    }

    // managers 또는 assistants 필드는 배열이므로 그대로 사용
    

    if (name === 'managers' || name === 'assistants') {
      setNewTask(prev => ({ ...prev, [name]: value }));
    } else {
      setNewTask(prev => ({ ...prev, [name]: formattedValue }));
    }
  }, []);

  const fetchSupervisorAssignmentDays = useCallback(async () => {
    try {
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth() + 1; // getMonth() is 0-indexed
      const response = await axiosInstance.get(`/schedule-entries/assignment-days/${year}/${month}`); // /api 제거
      setSupervisorAssignmentDays(response.data);
    } catch (error) {
      console.error('Error fetching supervisor assignment days:', error);
    }
  }, [setSupervisorAssignmentDays]);

  const fetchTasks = useCallback(async () => {
    try {
      const tasksResponse = await axiosInstance.get('/tasks');
      const fetchedTasks = tasksResponse.data;

      const tasksWithSupervisorChanges = await Promise.all(fetchedTasks.map(async task => {
        try {
          const supervisorChangesResponse = await axiosInstance.get(`/association-reports/supervisor-changes/${task.id}`);
          return { ...task, supervisorChangedDate: supervisorChangesResponse.data };
        } catch (error) {
          console.error(`Error fetching supervisor changes for task ${task.id}:`, error);
          return { ...task, supervisorChangedDate: null };
        }
      }));
      setTasks(tasksWithSupervisorChanges);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  }, [setTasks]);

  const fetchScheduleEntries = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/schedule-entries');
      setScheduleEntries(response.data);
    } catch (error) {
      console.error('Error fetching schedule entries:', error);
    }
  }, [setScheduleEntries]);

  const fetchSupervisors = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/supervisors'); 
      setSupervisorsList(response.data);
    } catch (error) {
      console.error('Error fetching supervisors:', error);
    }
  }, [setSupervisorsList]);

  const fetchContractors = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/contractors');
      setContractors(response.data);
    } catch (error) {
      console.error('Error fetching contractors:', error);
    }
  }, [setContractors]);

  const fetchBranches = useCallback(async () => {
    try {
      const response = await branchService.getBranches();
      setBranches(response.data);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  }, [setBranches]);

  useEffect(() => {
    if(user){
        fetchTasks(); // No year parameter
        fetchSupervisors();
        fetchScheduleEntries();
        fetchSupervisorAssignmentDays();
        fetchContractors();
        fetchBranches();
    }
  }, [user, fetchTasks, fetchSupervisors, fetchScheduleEntries, fetchSupervisorAssignmentDays, fetchContractors, fetchBranches]); // Removed selectedYear from dependencies

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const openDetailModal = (task, isEditMode = false) => {
    setSelectedTask(task);
    setIsTaskDetailEditMode(isEditMode);
    setIsDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setSelectedTask(null);
    setIsTaskDetailEditMode(false);
    setIsDetailModalOpen(false);
  };

  const handleAddTask = async (e) => {
    e.preventDefault();

    // 필수 필드 유효성 검사
    const requiredFields = ['displayNumber', 'name', 'number', 'period', 'company', 'contractDate', 'constructionPeriod', 'initialContractAmount', 'type'];
    for (const field of requiredFields) {
      if (!newTask[field] || (typeof newTask[field] === 'string' && newTask[field].trim() === '')) {
        Swal.fire({
          icon: 'warning',
          title: '입력 오류',
          text: '모든 필수 항목을 입력해주세요.',
        });
        return;
      }
    }

    // managers 배열 유효성 검사
    if (!newTask.managers || newTask.managers.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: '입력 오류',
        text: '책임감리원을 한 명 이상 선택해주세요.',
      });
      return;
    }

    try {
      await axiosInstance.post('/tasks', newTask); // /api 제거
      fetchTasks(selectedYear); // selectedYear 전달
            setNewTask({ displayNumber: '', name: '', number: '', managers: [], assistants: [], period: '', company: '', contractDate: '', constructionPeriod: '', remarks: '', contractAmount: '', completionAmount: '0', taskStatus: '공사중', completionReportStatus: '미완료' });
      closeModal();
      Swal.fire(
        '등록 완료!',
        '새 공사가 성공적으로 등록되었습니다.',
        'success'
      );
    } catch (error) {
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (Array.isArray(errorData)) {
          Swal.fire({
            icon: 'error',
            title: '오류',
            text: errorData.join('\n'),
          });
        } else if (typeof errorData === 'string') {
          Swal.fire({
            icon: 'error',
            title: '오류',
            text: errorData,
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: '오류',
            text: '공사 추가 중 오류가 발생했습니다.',
          });
        }
      } else {
        console.error('Error adding task:', error);
        Swal.fire({
          icon: 'error',
          title: '오류',
          text: '공사 추가 중 알 수 없는 오류가 발생했습니다.',
        });
      }
    }
  };

  const handleUpdateTask = async (updatedTask) => {
    try {
      await axiosInstance.put(`/tasks/${updatedTask.id}`, updatedTask);
      fetchTasks(selectedYear);
      closeDetailModal();
      Swal.fire(
        '수정 완료!',
        '공사 정보가 성공적으로 수정되었습니다.',
        'success'
      );
    } catch (error) {
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (Array.isArray(errorData)) {
          Swal.fire({
            icon: 'error',
            title: '오류',
            text: errorData.join('\n'),
          });
        } else if (typeof errorData === 'string') {
          Swal.fire({
            icon: 'error',
            title: '오류',
            text: errorData,
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: '오류',
            text: '공사 수정 중 오류가 발생했습니다.',
          });
        }
      } else {
        console.error('Error updating task:', error);
        Swal.fire({
          icon: 'error',
          title: '오류',
          text: '공사 수정 중 알 수 없는 오류가 발생했습니다.',
        });
      }
    }
  };

  const handleDeleteTask = async (taskId) => {
    const result = await Swal.fire({
      title: '정말로 이 공사를 삭제하시겠습니까?',
      text: "이 공사를 삭제하면 되돌릴 수 없습니다!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: '예, 삭제합니다!',
      cancelButtonText: '취소',
    });

    if (result.isConfirmed) {
      try {
        await axiosInstance.delete(`/tasks/${taskId}`); // /api 제거
        fetchTasks(selectedYear);
        fetchScheduleEntries(); // 추가
        fetchSupervisorAssignmentDays(); // 추가
        closeDetailModal();
        Swal.fire(
          '삭제됨!',
          '공사가 성공적으로 삭제되었습니다.',
          'success'
        );
      } catch (error) {
        console.error('Error deleting task:', error);
        Swal.fire({
          icon: 'error',
          title: '오류',
          text: '공사 삭제 중 오류가 발생했습니다.',
        });
      }
    }
  };

  return (
    <div className="App">
      {user && location.pathname !== '/login' && <NavigationBar />}
      <main className={location.pathname === '/login' ? 'main-content-login' : 'main-content'}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={<PrivateRoute />}>
            <Route index element={<MainPage />} />
            <Route path="homePage" element={<HomePage />} />
            <Route path="tasks" element={
              <>
                <TaskComponent 
                  tasks={tasks} 
                  onOpenModal={openModal} 
                  onTaskClick={(task) => openDetailModal(task, false)}
                  onEditTask={(task) => openDetailModal(task, true)}
                  onDeleteTask={handleDeleteTask}
                />
                <Modal 
                  isOpen={isModalOpen} 
                  onClose={closeModal}
                  onAddTask={handleAddTask}
                  taskData={newTask}
                  onTaskDataChange={handleInputChange}
                  supervisors={supervisorsList}
                  contractors={contractors}
                  branches={branches}
                />
                {selectedTask && (
                  <TaskDetailModal
                    isOpen={isDetailModalOpen}
                    onClose={closeDetailModal}
                    task={selectedTask}
                    onUpdateTask={handleUpdateTask}
                    onDeleteTask={handleDeleteTask}
                    supervisors={supervisorsList}
                    isEditMode={isTaskDetailEditMode}
                    contractors={contractors}
                  />
                )}
              </>
            } />
            <Route path="supervisors" element={<SupervisorPage />} />
            <Route path="schedule" element={<SchedulePage />} />
            <Route path="contractors" element={<ContractorManagementPage />} />
            <Route path="work-orders" element={<WorkOrderPage />} />
            <Route path="/users" element={<UserManagementPage />} />
            <Route path="/association-reports" element={<AssociationReportManagementPage />} />
            <Route path="/statistics" element={<StatisticsPage />} />
            <Route path="/company-info" element={<CompanyInfoPage />} />
            <Route path="/downloads" element={<DownloadsPage />} />
            <Route path="/branches" element={<BranchManagementPage />} />
          </Route>
        </Routes>
      </main>
    </div>
  );
}

export default App;